import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, Copy, Star, FileText, Package,
  Loader2, Settings, Eye, EyeOff, MousePointerClick,
  GripVertical, ChevronUp, ChevronDown, Lock, Unlock, RotateCcw,
  MapPin, PenLine,
} from 'lucide-react';

/* ─── Scale: A4 is 210×297mm. We render at 2.4px per mm ─── */
const MM = 2.4;
const PW = 210 * MM;
const PH = 297 * MM;
const MX = 15 * MM;
const CW = PW - 2 * MX;

/* ─── Sample values shown in preview ─── */
const SAMPLE = {
  customer_name: 'Customer Name Ltd',
  company_logo: '[Logo]',
  company_name: 'Cashel Blue',
  company_address: 'Beechmount, Fethard, Co. Tipperary',
  company_phone: '+353 52 613 1462',
  company_email: 'info@cashelblue.ie',
  dispatch_number: 'DN-2026-0042',
  dispatch_date: '15/02/2026',
  delivery_date: '17/02/2026',
  po_number: 'PO-98765',
  po_date: '10/02/2026',
  customer_address: '123 High Street\nDublin, D01 AB12\nIreland',
  vat_number: 'VAT: IE1234567T',
  vehicle_info: 'ABC Haulage',
  num_pallets: '2',
  total_cases: '24',
  total_weight: '48.50 kg',
  total_units: '120',
  footer_text: 'Cashel Blue | Beechmount, Fethard, Co. Tipperary | Tel: +353 52 613 1462',
  system_footer: 'System Generated Document - Dispatch Note - Voro Production Suite',
  customer_delivery_address: '456 Market Road\nCork, T12 XY34\nIreland',
  customer_contact_name: 'John Murphy',
  customer_contact_phone: '+353 1 234 5678',
  transporter_name: 'Fast Freight Ltd',
  transporter_address: '789 Logistics Park, Dublin',
  country_of_origin: 'Ireland',
  contents: 'Cashel Blue 1.5kg Wheel x 8\nCashel Blue 200g Wedge x 16',
  pallet_number: '1',
  total_pallets: '2',
  refrigeration_warning: 'KEEP REFRIGERATED 2-8\u00b0C',
};

/* ═══════════════════════════════════════════════
   DraggableField – wraps each field in the preview with drag support
   ═══════════════════════════════════════════════ */
function DraggableField({ el, selected, onSelect, onUpdatePosition, children, className = '', style = {} }) {
  const ref = useRef(null);
  const dragState = useRef(null);

  if (!el) return null;

  const isLocked = el.locked === true;
  const hasPosition = el.x_mm !== undefined && el.y_mm !== undefined;

  const handleMouseDown = (e) => {
    if (isLocked) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(el.id);
    const rect = ref.current?.parentElement?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: el.x_mm || 0,
      origY: el.y_mm || 0,
      parentRect: rect,
    };
    const handleMouseMove = (ev) => {
      if (!dragState.current) return;
      const dx = (ev.clientX - dragState.current.startX) / MM;
      const dy = (ev.clientY - dragState.current.startY) / MM;
      const newX = Math.max(0, Math.min(210, dragState.current.origX + dx));
      const newY = Math.max(0, Math.min(297, dragState.current.origY + dy));
      if (ref.current) {
        ref.current.style.left = `${newX * MM}px`;
        ref.current.style.top = `${newY * MM}px`;
      }
    };
    const handleMouseUp = (ev) => {
      if (!dragState.current) return;
      const dx = (ev.clientX - dragState.current.startX) / MM;
      const dy = (ev.clientY - dragState.current.startY) / MM;
      const newX = Math.max(0, Math.min(210, dragState.current.origX + dx));
      const newY = Math.max(0, Math.min(297, dragState.current.origY + dy));
      onUpdatePosition(el.id, newX, newY);
      dragState.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (el.visible === false) return (
    <div
      ref={ref}
      onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
      className={`absolute opacity-20 border border-dashed border-zinc-300 rounded cursor-pointer ${className}`}
      style={{ left: (el.x_mm || 0) * MM, top: (el.y_mm || 0) * MM, ...style }}
      data-testid={`field-${el.field_key || el.id}`}
    >
      <div className="text-[7px] text-zinc-400 px-1 flex items-center gap-0.5">
        <EyeOff className="w-2 h-2" /> {el.label}
      </div>
    </div>
  );

  return (
    <div
      ref={ref}
      onMouseDown={hasPosition ? handleMouseDown : undefined}
      onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
      className={`${hasPosition ? 'absolute' : 'relative'} transition-shadow rounded ${
        isLocked ? 'cursor-default' : (hasPosition ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer')
      } ${selected ? 'ring-2 ring-blue-500 ring-offset-1 bg-blue-50/40 z-20' : 'hover:ring-1 hover:ring-blue-300 z-10'} ${className}`}
      style={{
        ...(hasPosition ? { left: (el.x_mm || 0) * MM, top: (el.y_mm || 0) * MM } : {}),
        ...style,
      }}
      data-testid={`field-${el.field_key || el.id}`}
    >
      {selected && (
        <div className="absolute -top-3 left-0 text-[7px] bg-blue-500 text-white px-1.5 rounded-sm z-30 whitespace-nowrap flex items-center gap-0.5">
          {isLocked && <Lock className="w-2 h-2" />}
          {el.label}
        </div>
      )}
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SF – Simple selectable field (no drag, for flow-layout previews)
   ═══════════════════════════════════════════════ */
function SF({ el, selected, onSelect, children, className = '' }) {
  if (!el || el.visible === false) return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(el?.id); }}
      className={`relative opacity-30 border border-dashed border-zinc-300 rounded cursor-pointer ${className}`}
      data-testid={`field-${el?.field_key || el?.id}`}
    >
      <div className="absolute -top-2.5 left-1 text-[8px] bg-white px-1 text-zinc-400 flex items-center gap-0.5">
        <EyeOff className="w-2 h-2" /> {el?.label}
      </div>
      <div className="py-1 px-2 text-[9px] text-zinc-400 italic">Hidden</div>
    </div>
  );
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
      className={`relative cursor-pointer transition-all rounded ${selected ? 'ring-2 ring-blue-500 ring-offset-1 bg-blue-50/40' : 'hover:ring-1 hover:ring-blue-300'} ${className}`}
      data-testid={`field-${el.field_key || el.id}`}
    >
      {selected && <div className="absolute -top-2.5 left-1 text-[8px] bg-blue-500 text-white px-1.5 rounded-sm z-10">{el.label}</div>}
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Dispatch Note Preview – WYSIWYG A4 document
   ═══════════════════════════════════════════════ */
function DispatchNotePreview({ elements, selectedId, onSelect }) {
  const el = (key) => elements.find(e => e.field_key === key) || { visible: false, id: key, field_key: key, label: key };
  const vis = (key) => el(key).visible !== false;
  const s = (key, fallback) => {
    const e = el(key);
    return {
      fontSize: (e.fontSize || fallback || 10) * 0.7,
      fontWeight: e.fontWeight || 'normal',
      color: e.color || '#000',
      textAlign: e.textAlign || 'left',
    };
  };

  return (
    <div className="bg-white border border-zinc-300 shadow-lg relative overflow-hidden" style={{ width: PW, minHeight: PH, fontFamily: 'Helvetica, Arial, sans-serif' }} onClick={() => onSelect(null)} data-testid="design-canvas">

      {/* ─── HEADER SECTION ─── */}
      <div style={{ padding: `${12*MM}px ${MX}px ${4*MM}px` }}>
        <div className="flex justify-between items-start" style={{ minHeight: 10*MM }}>
          <SF el={el('customer_name')} selected={selectedId === el('customer_name').id} onSelect={onSelect} className="flex-1">
            <div style={{ ...s('customer_name', 20), lineHeight: 1.1 }}>{SAMPLE.customer_name}</div>
          </SF>
          {vis('company_logo') && (
            <SF el={el('company_logo')} selected={selectedId === el('company_logo').id} onSelect={onSelect}>
              <div className="border border-zinc-200 rounded bg-zinc-50 flex items-center justify-center"
                style={{ width: 35*MM, height: 12*MM, fontSize: 8*0.7, color: '#999' }}>
                Company Logo
              </div>
            </SF>
          )}
        </div>

        <SF el={el('dispatch_title') || { id: 'dn-3', field_key: 'dispatch_title', label: 'Document Title', visible: true }}
          selected={selectedId === (el('dispatch_title')?.id || 'dn-3')} onSelect={onSelect} className="mt-2">
          <div style={{ ...s('dispatch_title', 16), textAlign: 'center', letterSpacing: 1 }}>
            {(el('dispatch_title')?.text || 'DISPATCH NOTE')}
          </div>
        </SF>

        <div className="border-b-2 border-black" style={{ margin: `${2*MM}px 0` }} />

        <div className="flex gap-2" style={{ minHeight: 40*MM }}>
          <div className="flex-1" style={{ maxWidth: '48%' }}>
            <div className="border-2 border-black rounded-sm p-1.5" style={{ marginBottom: 3*MM }}>
              <SF el={el('dispatch_number')} selected={selectedId === el('dispatch_number').id} onSelect={onSelect}>
                <div className="flex items-baseline gap-1">
                  <span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>Dispatch Note #:</span>
                  <span style={{ ...s('dispatch_number', 14) }}>{SAMPLE.dispatch_number}</span>
                </div>
              </SF>
              <SF el={el('dispatch_date')} selected={selectedId === el('dispatch_date').id} onSelect={onSelect} className="mt-0.5">
                <div className="flex items-baseline gap-1">
                  <span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>Delivery Date:</span>
                  <span style={{ ...s('dispatch_date', 10) }}>{SAMPLE.dispatch_date}</span>
                </div>
              </SF>
            </div>
            <SF el={el('po_number')} selected={selectedId === el('po_number').id} onSelect={onSelect}>
              <div className="flex items-baseline gap-1">
                <span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>PO Number:</span>
                <span style={{ ...s('po_number', 10) }}>{SAMPLE.po_number}</span>
              </div>
            </SF>
            <SF el={el('po_date')} selected={selectedId === el('po_date').id} onSelect={onSelect} className="mt-0.5">
              <div className="flex items-baseline gap-1">
                <span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>PO Date:</span>
                <span style={{ ...s('po_date', 10) }}>{SAMPLE.po_date}</span>
              </div>
            </SF>
          </div>

          <div className="border-l border-zinc-300 mx-1" />

          <div className="flex-1">
            <SF el={el('customer_address')} selected={selectedId === el('customer_address').id} onSelect={onSelect}>
              <div style={{ fontSize: 11*0.7, fontWeight: 'bold', marginBottom: 2 }}>DELIVERY ADDRESS:</div>
              <div style={{ ...s('customer_address', 10), whiteSpace: 'pre-line', lineHeight: 1.4 }}>{SAMPLE.customer_address}</div>
            </SF>
            {vis('vat_number') && (
              <SF el={el('vat_number')} selected={selectedId === el('vat_number').id} onSelect={onSelect} className="mt-1">
                <div style={{ ...s('vat_number', 10) }}>{SAMPLE.vat_number}</div>
              </SF>
            )}
          </div>
        </div>
      </div>

      {/* ─── BODY SECTION ─── */}
      <div style={{ padding: `${2*MM}px ${MX}px` }}>
        {(vis('vehicle_info') || vis('num_pallets')) && (
          <div className="flex gap-4 mb-1">
            {vis('vehicle_info') && (
              <SF el={el('vehicle_info')} selected={selectedId === el('vehicle_info').id} onSelect={onSelect}>
                <span style={{ fontSize: 9*0.7, fontWeight: 'bold' }}>Vehicle: </span>
                <span style={{ ...s('vehicle_info', 9) }}>{SAMPLE.vehicle_info}</span>
              </SF>
            )}
            {vis('num_pallets') && (
              <SF el={el('num_pallets')} selected={selectedId === el('num_pallets').id} onSelect={onSelect}>
                <span style={{ fontSize: 9*0.7, fontWeight: 'bold' }}>Pallets: </span>
                <span style={{ ...s('num_pallets', 9) }}>{SAMPLE.num_pallets}</span>
              </SF>
            )}
          </div>
        )}

        {vis('line_items_table') && (
          <SF el={el('line_items_table')} selected={selectedId === el('line_items_table').id} onSelect={onSelect} className="mt-1">
            <table className="w-full border-collapse" style={{ fontSize: 9*0.7 }}>
              <thead>
                <tr style={{ backgroundColor: '#f2f2cc' }}>
                  {['Product', 'Supp Code', 'Batch', 'Use By', 'Units', 'Cases', 'Wt (kg)', 'Notes'].map(h => (
                    <th key={h} className="border border-black px-1 py-0.5 text-left font-bold" style={{ fontSize: 9*0.7 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Cashel Blue 1.5kg', 'CB-150', 'B2026-01', '15/06/26', '8', '4.0', '12.00', ''],
                  ['Cashel Blue 200g', 'CB-200', 'B2026-01', '15/06/26', '16', '2.0', '3.20', ''],
                  ['Cashel Blue 1.5kg', 'CB-150', 'B2026-02', '22/06/26', '8', '4.0', '12.00', 'QC Released'],
                ].map((row, i) => (
                  <tr key={i} style={{ backgroundColor: '#fffae6' }}>
                    {row.map((cell, j) => (
                      <td key={j} className="border border-black px-1 py-0.5" style={{ textAlign: j >= 4 ? 'right' : 'left' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </SF>
        )}

        <div className="flex gap-3 mt-2">
          <div className="flex-1">
            <table className="border-collapse" style={{ fontSize: 9*0.7 }}>
              <thead>
                <tr style={{ backgroundColor: '#e5e5e5' }}>
                  <th className="border border-black px-1 py-0.5 text-left">Product</th>
                  <th className="border border-black px-1 py-0.5 text-right">Cases</th>
                  <th className="border border-black px-1 py-0.5 text-right">Net Wt</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border border-black px-1 py-0.5">Cashel Blue 1.5kg</td><td className="border border-black px-1 py-0.5 text-right">8.0</td><td className="border border-black px-1 py-0.5 text-right">24.00</td></tr>
                <tr><td className="border border-black px-1 py-0.5">Cashel Blue 200g</td><td className="border border-black px-1 py-0.5 text-right">2.0</td><td className="border border-black px-1 py-0.5 text-right">3.20</td></tr>
              </tbody>
            </table>
          </div>
          <div className="flex-shrink-0 text-right space-y-0.5">
            {vis('total_cases') && (
              <SF el={el('total_cases')} selected={selectedId === el('total_cases').id} onSelect={onSelect}>
                <div><span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>Total Cases: </span><span style={s('total_cases', 10)}>{SAMPLE.total_cases}</span></div>
              </SF>
            )}
            {vis('total_weight') && (
              <SF el={el('total_weight')} selected={selectedId === el('total_weight').id} onSelect={onSelect}>
                <div><span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>Net Weight: </span><span style={s('total_weight', 12)}>{SAMPLE.total_weight}</span></div>
              </SF>
            )}
            {vis('total_units') && (
              <SF el={el('total_units')} selected={selectedId === el('total_units').id} onSelect={onSelect}>
                <div><span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>Total Units: </span><span style={s('total_units', 10)}>{SAMPLE.total_units}</span></div>
              </SF>
            )}
          </div>
        </div>
      </div>

      {/* Signature */}
      {vis('signature_section') && (
        <div style={{ padding: `${4*MM}px ${MX}px` }}>
          <SF el={el('signature_section')} selected={selectedId === el('signature_section').id} onSelect={onSelect}>
            <div className="flex gap-6 mt-2">
              <div className="flex-1 border-b border-black pt-6 text-center" style={{ fontSize: 8*0.7 }}>Received by (Print Name)</div>
              <div className="flex-1 border-b border-black pt-6 text-center" style={{ fontSize: 8*0.7 }}>Signature</div>
              <div className="flex-1 border-b border-black pt-6 text-center" style={{ fontSize: 8*0.7 }}>Date / Time</div>
            </div>
          </SF>
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: `${4*MM}px ${MX}px`, position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        {vis('footer_text') && (
          <SF el={el('footer_text')} selected={selectedId === el('footer_text').id} onSelect={onSelect}>
            <div style={{ ...s('footer_text', 8), textAlign: 'center' }}>{SAMPLE.footer_text}</div>
          </SF>
        )}
        {vis('system_footer') && (
          <SF el={el('system_footer')} selected={selectedId === el('system_footer').id} onSelect={onSelect} className="mt-0.5">
            <div style={{ ...s('system_footer', 6), textAlign: 'center' }}>{SAMPLE.system_footer}</div>
          </SF>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Pallet Label Preview – WYSIWYG A4 document
   ═══════════════════════════════════════════════ */
function PalletLabelPreview({ elements, selectedId, onSelect }) {
  const el = (key) => elements.find(e => e.field_key === key) || { visible: false, id: key, field_key: key, label: key };
  const vis = (key) => el(key).visible !== false;
  const s = (key, fallback) => {
    const e = el(key);
    return {
      fontSize: (e.fontSize || fallback || 10) * 0.7,
      fontWeight: e.fontWeight || 'normal',
      color: e.color || '#000',
    };
  };

  return (
    <div className="bg-white border-2 border-black shadow-lg relative" style={{ width: PW, minHeight: PH, fontFamily: 'Helvetica, Arial, sans-serif' }} onClick={() => onSelect(null)} data-testid="design-canvas">
      {vis('company_logo') && (
        <div className="border-b-2 border-black" style={{ padding: `${6*MM}px ${MX}px` }}>
          <SF el={el('company_logo')} selected={selectedId === el('company_logo').id} onSelect={onSelect}>
            <div className="border border-zinc-200 rounded bg-zinc-50 flex items-center justify-center mx-auto"
              style={{ width: 50*MM, height: 16*MM, fontSize: 9*0.7, color: '#999' }}>Company Logo</div>
          </SF>
        </div>
      )}

      <div className="border-b-2 border-black" style={{ padding: `${4*MM}px ${MX}px` }}>
        <div className="text-center mb-1" style={{ fontSize: 8*0.7, fontWeight: 'bold', color: '#666', letterSpacing: 1 }}>DELIVER TO:</div>
        <SF el={el('customer_name')} selected={selectedId === el('customer_name').id} onSelect={onSelect}>
          <div className="text-center" style={{ ...s('customer_name', 18), lineHeight: 1.2 }}>{SAMPLE.customer_name}</div>
        </SF>
        {vis('customer_delivery_address') && (
          <SF el={el('customer_delivery_address')} selected={selectedId === el('customer_delivery_address').id} onSelect={onSelect} className="mt-1">
            <div className="text-center" style={{ ...s('customer_delivery_address', 11), whiteSpace: 'pre-line', lineHeight: 1.4 }}>{SAMPLE.customer_delivery_address}</div>
          </SF>
        )}
        <div className="flex justify-center gap-6 mt-1">
          {vis('customer_contact_name') && (
            <SF el={el('customer_contact_name')} selected={selectedId === el('customer_contact_name').id} onSelect={onSelect}>
              <span style={{ fontSize: 9*0.7, fontWeight: 'bold' }}>Contact: </span>
              <span style={{ ...s('customer_contact_name', 10) }}>{SAMPLE.customer_contact_name}</span>
            </SF>
          )}
          {vis('customer_contact_phone') && (
            <SF el={el('customer_contact_phone')} selected={selectedId === el('customer_contact_phone').id} onSelect={onSelect}>
              <span style={{ fontSize: 9*0.7, fontWeight: 'bold' }}>Tel: </span>
              <span style={{ ...s('customer_contact_phone', 10) }}>{SAMPLE.customer_contact_phone}</span>
            </SF>
          )}
        </div>
      </div>

      <div className="border-b-2 border-black" style={{ padding: `${4*MM}px ${MX}px` }}>
        <div style={{ fontSize: 8*0.7, fontWeight: 'bold', color: '#666', letterSpacing: 1, marginBottom: 2 }}>TRANSPORTER:</div>
        {vis('transporter_name') && (
          <SF el={el('transporter_name')} selected={selectedId === el('transporter_name').id} onSelect={onSelect}>
            <div style={{ ...s('transporter_name', 11) }}>{SAMPLE.transporter_name}</div>
          </SF>
        )}
        {vis('transporter_address') && (
          <SF el={el('transporter_address')} selected={selectedId === el('transporter_address').id} onSelect={onSelect}>
            <div style={{ ...s('transporter_address', 10) }}>{SAMPLE.transporter_address}</div>
          </SF>
        )}
      </div>

      <div className="border-b-2 border-black" style={{ padding: `${4*MM}px ${MX}px` }}>
        {vis('country_of_origin') && (
          <SF el={el('country_of_origin')} selected={selectedId === el('country_of_origin').id} onSelect={onSelect}>
            <span style={{ fontSize: 9*0.7, fontWeight: 'bold' }}>Country of Origin: </span>
            <span style={{ ...s('country_of_origin', 11) }}>{SAMPLE.country_of_origin}</span>
          </SF>
        )}
        {vis('contents') && (
          <SF el={el('contents')} selected={selectedId === el('contents').id} onSelect={onSelect} className="mt-1">
            <div style={{ fontSize: 9*0.7, fontWeight: 'bold', marginBottom: 2 }}>Contents:</div>
            <div style={{ ...s('contents', 12), whiteSpace: 'pre-line', lineHeight: 1.4, padding: '2px 4px', border: '1px solid #ccc', backgroundColor: '#fafafa', borderRadius: 2 }}>{SAMPLE.contents}</div>
          </SF>
        )}
      </div>

      <div className="border-b-2 border-black" style={{ padding: `${4*MM}px ${MX}px` }}>
        {vis('po_number') && (
          <SF el={el('po_number')} selected={selectedId === el('po_number').id} onSelect={onSelect}>
            <span style={{ fontSize: 10*0.7, fontWeight: 'bold' }}>PO Number: </span>
            <span style={{ ...s('po_number', 11) }}>{SAMPLE.po_number}</span>
          </SF>
        )}
        <div className="flex justify-between mt-1">
          {vis('pallet_number') && (
            <SF el={el('pallet_number')} selected={selectedId === el('pallet_number').id} onSelect={onSelect}>
              <div className="border-2 border-black rounded px-2 py-1 text-center">
                <div style={{ fontSize: 8*0.7, fontWeight: 'bold' }}>PALLET</div>
                <div style={{ ...s('pallet_number', 14) }}>{SAMPLE.pallet_number}</div>
              </div>
            </SF>
          )}
          {vis('total_pallets') && (
            <SF el={el('total_pallets')} selected={selectedId === el('total_pallets').id} onSelect={onSelect}>
              <div className="border-2 border-black rounded px-2 py-1 text-center">
                <div style={{ fontSize: 8*0.7, fontWeight: 'bold' }}>OF TOTAL</div>
                <div style={{ ...s('total_pallets', 14) }}>{SAMPLE.total_pallets}</div>
              </div>
            </SF>
          )}
          {vis('total_cases') && (
            <SF el={el('total_cases')} selected={selectedId === el('total_cases').id} onSelect={onSelect}>
              <div className="border-2 border-black rounded px-2 py-1 text-center">
                <div style={{ fontSize: 8*0.7, fontWeight: 'bold' }}>TOTAL CASES</div>
                <div style={{ ...s('total_cases', 12) }}>{SAMPLE.total_cases}</div>
              </div>
            </SF>
          )}
        </div>
      </div>

      {vis('refrigeration_warning') && (
        <div style={{ padding: `${6*MM}px ${MX}px` }}>
          <SF el={el('refrigeration_warning')} selected={selectedId === el('refrigeration_warning').id} onSelect={onSelect}>
            <div className="border-2 border-red-600 rounded p-2 text-center" style={{ ...s('refrigeration_warning', 11), color: el('refrigeration_warning').color || '#cc0000', backgroundColor: '#fff5f5' }}>
              {SAMPLE.refrigeration_warning}
            </div>
          </SF>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Aldi Label Preview – Drag-and-drop WYSIWYG A4
   ═══════════════════════════════════════════════ */
function AldiLabelPreview({ elements, selectedId, onSelect, onUpdatePosition }) {
  const el = (key) => elements.find(e => e.field_key === key) || { visible: false, id: key, field_key: key, label: key };
  /* Scale factor: A4 at 2.4px/mm → preview font ≈ 0.7× real pt size */
  const S = 0.7;
  const fs = (key) => (el(key).fontSize || 10) * S;
  const fw = (key) => el(key).fontWeight || 'normal';
  const fc = (key) => el(key).color || '#000';

  return (
    <div className="bg-white border-2 border-black shadow-lg relative overflow-hidden"
      style={{ width: PW, height: PH, fontFamily: 'Helvetica, Arial, sans-serif' }}
      onClick={() => onSelect(null)} data-testid="design-canvas-aldi">

      {/* Logo placeholder */}
      <DraggableField el={el('company_logo')} selected={selectedId === el('company_logo').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div className="border border-zinc-200 rounded bg-zinc-50 flex items-center justify-center"
          style={{ width: 35*MM, height: 14*MM, fontSize: fs('company_logo'), color: '#999' }}>Company Logo</div>
      </DraggableField>

      {/* FROM box */}
      <DraggableField el={el('from_address')} selected={selectedId === el('from_address').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div className="border-2 border-black rounded-sm" style={{ width: 80*MM, padding: '3px 6px' }}>
          <div style={{ fontSize: fs('from_address') + 1, fontWeight: 'bold', marginBottom: 2 }}>From:</div>
          <div style={{ fontSize: fs('from_address'), fontWeight: fw('from_address'), whiteSpace: 'pre-line', lineHeight: 1.5, color: fc('from_address') }}>
            {el('from_address').text || 'Artisan Cheese Ltd (Eur)\nC/O WFHC Curranstown\nIRL - X35 Y670\nARKLOW'}
          </div>
        </div>
      </DraggableField>

      {/* Supplier Number */}
      <DraggableField el={el('supplier_number')} selected={selectedId === el('supplier_number').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('supplier_number'), color: fc('supplier_number') }}>
          <span style={{ fontWeight: 'bold' }}>Supplier Number : </span>
          <span style={{ fontWeight: fw('supplier_number') }}>{el('supplier_number').text || '04854'}</span>
        </div>
      </DraggableField>

      {/* TO box */}
      <DraggableField el={el('to_address')} selected={selectedId === el('to_address').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div className="border-2 border-black rounded-sm" style={{ width: 80*MM, padding: '3px 6px' }}>
          <div style={{ fontSize: fs('to_address') + 1, fontWeight: 'bold', marginBottom: 2 }}>To:</div>
          <div style={{ fontSize: fs('to_address'), fontWeight: fw('to_address'), whiteSpace: 'pre-line', lineHeight: 1.5, color: fc('to_address') }}>
            {el('to_address').text || 'Aldi Stores (Ireland) Ltd.\nMitchelstown\nLimerick Road\nIRL-P67 EY88\nMitchelstown, Co. Cork'}
          </div>
        </div>
      </DraggableField>

      {/* Contact Info */}
      <DraggableField el={el('contact_info')} selected={selectedId === el('contact_info').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('contact_info'), fontWeight: fw('contact_info'), color: fc('contact_info') }}>
          {el('contact_info').text || 'Nora Griffin    Tel 00353 45 846326    Availability.naa@aldi.ie'}
        </div>
      </DraggableField>

      {/* DELIVERY DOCKET title */}
      <DraggableField el={el('delivery_docket_title')} selected={selectedId === el('delivery_docket_title').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{
          fontSize: fs('delivery_docket_title'),
          fontWeight: fw('delivery_docket_title'),
          color: fc('delivery_docket_title'),
          textAlign: el('delivery_docket_title').textAlign || 'center',
          width: CW,
          borderBottom: '3px solid black',
          paddingBottom: 4,
        }}>
          {el('delivery_docket_title').text || 'DELIVERY DOCKET'}
        </div>
      </DraggableField>

      {/* NO / PO Number */}
      <DraggableField el={el('dispatch_number')} selected={selectedId === el('dispatch_number').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('dispatch_number'), fontWeight: fw('dispatch_number'), color: fc('dispatch_number') }}>NO: {SAMPLE.dispatch_number}</div>
      </DraggableField>

      <DraggableField el={el('po_number')} selected={selectedId === el('po_number').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('po_number'), fontWeight: fw('po_number'), color: fc('po_number') }}>PO NUMBER: {SAMPLE.po_number}</div>
      </DraggableField>

      {/* Product Table */}
      <DraggableField el={el('product_table')} selected={selectedId === el('product_table').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <table className="border-collapse" style={{ fontSize: fs('product_table'), width: CW }}>
          <thead>
            <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
              {['PRODUCT\nNUMBER', 'DESCRIPTION', 'QTY', 'UNIT', 'USE BY'].map(h => (
                <th key={h} className="border border-zinc-600 px-1 py-0.5 text-left font-bold" style={{ fontSize: fs('product_table') - 0.5, whiteSpace: 'pre-line' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[['716880', 'S/S CASHELBLUE 150G', '216', 'CASE(S)', '15/06/26'],
              ['716881', 'CASHELBLUE 200G WDG', '48', 'CASE(S)', '22/06/26']].map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#f4f6f7' : 'white' }}>
                {row.map((cell, j) => (
                  <td key={j} className="border border-zinc-300 px-1 py-0.5">{cell}</td>
                ))}
              </tr>
            ))}
            <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
              <td className="border px-1 py-0.5 font-bold">TOTAL</td>
              <td className="border px-1 py-0.5"></td>
              <td className="border px-1 py-0.5 font-bold">264</td>
              <td className="border px-1 py-0.5 font-bold">CASE(S)</td>
              <td className="border px-1 py-0.5"></td>
            </tr>
          </tbody>
        </table>
      </DraggableField>

      {/* Use By Dates */}
      <DraggableField el={el('use_by_dates')} selected={selectedId === el('use_by_dates').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('use_by_dates'), fontWeight: fw('use_by_dates'), color: fc('use_by_dates') }}>
          <span style={{ fontWeight: 'bold' }}>USE BY: </span>
          <span>15/06/2026</span>
          <br />
          <span style={{ marginLeft: fs('use_by_dates') * 5 }}>22/06/2026</span>
        </div>
      </DraggableField>

      {/* Estimated Pallets */}
      <DraggableField el={el('estimated_pallets')} selected={selectedId === el('estimated_pallets').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('estimated_pallets'), fontWeight: fw('estimated_pallets'), color: fc('estimated_pallets') }}>ESTIMATED PALLETS - 2</div>
      </DraggableField>

      {/* Delivery Date */}
      <DraggableField el={el('delivery_date')} selected={selectedId === el('delivery_date').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('delivery_date'), fontWeight: fw('delivery_date'), color: fc('delivery_date') }}>DELIVERY DATE : {SAMPLE.dispatch_date}</div>
      </DraggableField>

      {/* Storage Info */}
      <DraggableField el={el('storage_info')} selected={selectedId === el('storage_info').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('storage_info'), fontWeight: fw('storage_info'), color: fc('storage_info') }}>
          {el('storage_info').text || 'Storage: Chilled 0-6\u00b0C'}
        </div>
      </DraggableField>

      {/* Pallet / Country */}
      <DraggableField el={el('pallet_info')} selected={selectedId === el('pallet_info').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('pallet_info'), fontWeight: fw('pallet_info'), color: fc('pallet_info') }}>Pallet 1 of 2    |    Country of Origin: Ireland</div>
      </DraggableField>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Agropur Label Preview – Drag-and-drop WYSIWYG A4
   ═══════════════════════════════════════════════ */
function AgropurLabelPreview({ elements, selectedId, onSelect, onUpdatePosition }) {
  const el = (key) => elements.find(e => e.field_key === key) || { visible: false, id: key, field_key: key, label: key };
  const S = 0.7;
  const fs = (key) => (el(key).fontSize || 10) * S;
  const fw = (key) => el(key).fontWeight || 'normal';
  const fc = (key) => el(key).color || '#000';

  return (
    <div className="bg-white border-2 border-black shadow-lg relative overflow-hidden"
      style={{ width: PW, height: PH, fontFamily: 'Helvetica, Arial, sans-serif' }}
      onClick={() => onSelect(null)} data-testid="design-canvas-agropur">

      {/* Logo */}
      <DraggableField el={el('company_logo')} selected={selectedId === el('company_logo').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div className="border border-zinc-200 rounded bg-zinc-50 flex items-center justify-center"
          style={{ width: 45*MM, height: 18*MM, fontSize: fs('company_logo'), color: '#999' }}>Cashel Blue Logo</div>
      </DraggableField>

      {/* AGROPUR name */}
      <DraggableField el={el('customer_name')} selected={selectedId === el('customer_name').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{
          fontSize: fs('customer_name'),
          fontWeight: fw('customer_name'),
          textAlign: el('customer_name').textAlign || 'center',
          width: CW,
          color: fc('customer_name'),
        }}>
          {el('customer_name').text || 'AGROPUR'}
        </div>
      </DraggableField>

      {/* Address Line 1 */}
      <DraggableField el={el('customer_address_line1')} selected={selectedId === el('customer_address_line1').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('customer_address_line1'), fontWeight: fw('customer_address_line1'), textAlign: el('customer_address_line1').textAlign || 'center', width: CW, color: fc('customer_address_line1') }}>
          {el('customer_address_line1').text || '4600 rue Armand-Frappier,'}
        </div>
      </DraggableField>

      {/* Address Line 2 */}
      <DraggableField el={el('customer_address_line2')} selected={selectedId === el('customer_address_line2').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('customer_address_line2'), fontWeight: fw('customer_address_line2'), textAlign: el('customer_address_line2').textAlign || 'center', width: CW, color: fc('customer_address_line2') }}>
          {el('customer_address_line2').text || 'St-Hubert, QC, J3Z 1G5'}
        </div>
      </DraggableField>

      {/* Country */}
      <DraggableField el={el('customer_country')} selected={selectedId === el('customer_country').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('customer_country'), fontWeight: fw('customer_country'), textAlign: el('customer_country').textAlign || 'center', width: CW, color: fc('customer_country') }}>
          {el('customer_country').text || 'CANADA'}
        </div>
      </DraggableField>

      {/* COA Email */}
      <DraggableField el={el('coa_email')} selected={selectedId === el('coa_email').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('coa_email'), fontWeight: fw('coa_email'), color: fc('coa_email') }}>
          {el('coa_email').text || 'COA (Envoyer \u00e0/Send to) : HUB.ImportsQA@agropur.com'}
        </div>
      </DraggableField>

      {/* Supplier Name */}
      <DraggableField el={el('supplier_name')} selected={selectedId === el('supplier_name').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('supplier_name'), color: fc('supplier_name') }}>
          <span style={{ fontWeight: 'bold' }}>Fournisseur/Supplier : </span>
          <span style={{ fontWeight: fw('supplier_name') }}>{el('supplier_name').text || 'CASHEL FARMHOUSE CHEESEMAKERS, IRELAND'}</span>
        </div>
      </DraggableField>

      {/* PO Number */}
      <DraggableField el={el('po_number')} selected={selectedId === el('po_number').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('po_number'), color: fc('po_number') }}>
          <span style={{ fontWeight: 'bold' }}>PO # : </span>
          <span style={{ fontWeight: fw('po_number') }}>{SAMPLE.po_number}</span>
        </div>
      </DraggableField>

      {/* Product Table */}
      <DraggableField el={el('product_table')} selected={selectedId === el('product_table').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <table className="border-collapse" style={{ fontSize: fs('product_table'), width: CW }}>
          <thead>
            <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
              {['Product', 'Batch', 'Net Wt (kg)', 'Cases'].map(h => (
                <th key={h} className="border border-zinc-600 px-1 py-0.5 text-left font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[['Cashel Blue 1.5kg', 'B2026-01', '12.00', '4'],
              ['Cashel Blue 200g', 'B2026-02', '3.20', '2']].map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#f4f6f7' : 'white' }}>
                {row.map((cell, j) => (
                  <td key={j} className="border border-zinc-300 px-1 py-0.5">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </DraggableField>

      {/* Production Dates */}
      <DraggableField el={el('production_dates')} selected={selectedId === el('production_dates').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('production_dates'), fontWeight: fw('production_dates'), color: fc('production_dates') }}>
          <span style={{ fontWeight: 'bold' }}>Date(s) de Production : </span>
          <span>10/02/2026</span>
        </div>
      </DraggableField>

      {/* Use By Dates */}
      <DraggableField el={el('use_by_dates')} selected={selectedId === el('use_by_dates').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('use_by_dates'), fontWeight: fw('use_by_dates'), color: fc('use_by_dates') }}>
          <span style={{ fontWeight: 'bold' }}>Date(s) d'Expiration : </span>
          <span>15/06/2026</span>
        </div>
      </DraggableField>

      {/* Storage */}
      <DraggableField el={el('storage_info')} selected={selectedId === el('storage_info').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('storage_info'), fontWeight: fw('storage_info'), color: fc('storage_info') }}>
          {el('storage_info').text || 'Storage: Chilled 0-6\u00b0C'}
        </div>
      </DraggableField>

      {/* Net Weight */}
      <DraggableField el={el('total_weight')} selected={selectedId === el('total_weight').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('total_weight'), fontWeight: fw('total_weight'), color: fc('total_weight') }}>
          Poids Net / Net Weight: {SAMPLE.total_weight}
        </div>
      </DraggableField>

      {/* Pallet Info */}
      <DraggableField el={el('pallet_info')} selected={selectedId === el('pallet_info').id}
        onSelect={onSelect} onUpdatePosition={onUpdatePosition}>
        <div style={{ fontSize: fs('pallet_info'), fontWeight: fw('pallet_info'), color: fc('pallet_info') }}>Pallet 1 of 2    |    Country of Origin: Ireland</div>
      </DraggableField>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Properties Panel (right sidebar)
   ═══════════════════════════════════════════════ */
function PropertiesPanel({ element, onChange, onDelete, isDraggableTemplate }) {
  if (!element) return (
    <div className="flex flex-col items-center justify-center h-32 text-xs text-zinc-400 gap-2">
      <MousePointerClick className="w-5 h-5" />
      <span>Click a field in the preview to edit its properties</span>
    </div>
  );
  const u = (key, val) => onChange({ ...element, [key]: val });
  return (
    <div className="space-y-3 text-xs" data-testid="properties-panel">
      <h3 className="font-semibold text-zinc-700 flex items-center gap-1 text-sm">
        <Settings className="w-4 h-4" /> {element.label}
      </h3>

      {/* Text Content (editable for text type) */}
      {element.type === 'text' && (
        <div>
          <Label className="text-[11px]">Text Content</Label>
          <Textarea value={element.text || ''} onChange={e => u('text', e.target.value)} className="text-xs mt-1 min-h-[60px]" rows={3}
            data-testid="prop-text-content" />
        </div>
      )}

      {/* Font controls */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Font Size</Label>
          <Input type="number" value={element.fontSize || 10} onChange={e => u('fontSize', +e.target.value)} className="h-7 text-xs mt-1" min={6} max={48} />
        </div>
        <div>
          <Label className="text-[11px]">Weight</Label>
          <Select value={element.fontWeight || 'normal'} onValueChange={v => u('fontWeight', v)}>
            <SelectTrigger className="h-7 text-xs mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Color */}
      <div>
        <Label className="text-[11px]">Text Color</Label>
        <div className="flex gap-1 mt-1">
          <input type="color" value={element.color || '#000000'} onChange={e => u('color', e.target.value)} className="w-7 h-7 rounded border cursor-pointer" />
          <Input value={element.color || '#000000'} onChange={e => u('color', e.target.value)} className="h-7 text-xs flex-1" />
        </div>
      </div>

      {/* Alignment */}
      <div>
        <Label className="text-[11px]">Alignment</Label>
        <div className="flex gap-1 mt-1">
          {['left', 'center', 'right'].map(a => (
            <button key={a} onClick={() => u('textAlign', a)}
              className={`flex-1 py-1 px-2 rounded text-[10px] border transition-colors ${element.textAlign === a ? 'bg-blue-100 border-blue-300 text-blue-700' : 'hover:bg-zinc-100 border-zinc-200'}`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Position (for draggable templates) */}
      {isDraggableTemplate && element.x_mm !== undefined && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t">
          <div>
            <Label className="text-[11px]">X (mm)</Label>
            <Input type="number" value={Math.round(element.x_mm || 0)} onChange={e => u('x_mm', +e.target.value)}
              className="h-7 text-xs mt-1" min={0} max={210} disabled={element.locked}
              data-testid="prop-x-mm" />
          </div>
          <div>
            <Label className="text-[11px]">Y (mm)</Label>
            <Input type="number" value={Math.round(element.y_mm || 0)} onChange={e => u('y_mm', +e.target.value)}
              className="h-7 text-xs mt-1" min={0} max={297} disabled={element.locked}
              data-testid="prop-y-mm" />
          </div>
        </div>
      )}

      {/* Lock / Visibility */}
      <div className="space-y-2 pt-1 border-t">
        {isDraggableTemplate && (
          <div className="flex items-center gap-2">
            <Switch checked={element.locked === true} onCheckedChange={v => u('locked', v)} id="lock" />
            <Label htmlFor="lock" className="text-[11px] cursor-pointer flex items-center gap-1">
              {element.locked ? <><Lock className="w-3 h-3 text-amber-600" /> Locked</> : <><Unlock className="w-3 h-3 text-zinc-400" /> Unlocked</>}
            </Label>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Switch checked={element.visible !== false} onCheckedChange={v => u('visible', v)} id="vis" />
          <Label htmlFor="vis" className="text-[11px] cursor-pointer">{element.visible !== false ? 'Visible' : 'Hidden'}</Label>
        </div>
      </div>

      <Button variant="destructive" size="sm" className="w-full h-7 text-xs mt-2" onClick={onDelete} data-testid="delete-element">
        <Trash2 className="w-3 h-3 mr-1" /> Remove Field
      </Button>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Field List (left sidebar)
   ═══════════════════════════════════════════════ */
function FieldList({ elements, selectedId, onSelect, onToggle, onMove, isDraggable, onToggleLock }) {
  const sections = ['header', 'body', 'footer'];
  const sectionLabels = { header: 'Header', body: 'Body', footer: 'Footer' };
  return (
    <div className="space-y-3" data-testid="field-list">
      {sections.map(sec => {
        const secEls = elements.filter(e => e.section === sec).sort((a, b) => (a.order || 0) - (b.order || 0));
        if (!secEls.length) return null;
        return (
          <div key={sec}>
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 px-1">{sectionLabels[sec]}</h4>
            <div className="space-y-0.5">
              {secEls.map((el, idx) => (
                <div key={el.id}
                  className={`flex items-center gap-1 px-1.5 py-1 rounded text-[11px] cursor-pointer transition-colors ${
                    selectedId === el.id ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-zinc-50'
                  } ${el.visible === false ? 'opacity-40' : ''}`}
                  onClick={() => onSelect(el.id)}
                  data-testid={`fieldlist-${el.field_key || el.id}`}
                >
                  <GripVertical className="w-3 h-3 text-zinc-300 flex-shrink-0" />
                  {el.type === 'field' ? <FileText className="w-3 h-3 flex-shrink-0" /> : <span className="w-3 h-3 flex-shrink-0 text-center text-[8px] font-bold">T</span>}
                  <span className="truncate flex-1">{el.label || el.field_key}</span>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {isDraggable && (
                      <button onClick={(e) => { e.stopPropagation(); onToggleLock?.(el.id); }} className="p-0.5 hover:bg-zinc-200 rounded" title={el.locked ? 'Unlock' : 'Lock'}>
                        {el.locked ? <Lock className="w-3 h-3 text-amber-600" /> : <Unlock className="w-3 h-3 text-zinc-300" />}
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); onMove(el.id, -1); }} className="p-0.5 hover:bg-zinc-200 rounded" disabled={idx === 0}>
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onMove(el.id, 1); }} className="p-0.5 hover:bg-zinc-200 rounded" disabled={idx === secEls.length - 1}>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onToggle(el.id); }} className="p-0.5 hover:bg-zinc-200 rounded">
                      {el.visible !== false ? <Eye className="w-3 h-3 text-green-600" /> : <EyeOff className="w-3 h-3 text-zinc-400" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN: Template Designer Page
   ═══════════════════════════════════════════════ */
export default function TemplateDesigner() {
  const { user } = useAuth();
  const [templateType, setTemplateType] = useState('dispatch_note');
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDialog, setNewDialog] = useState(false);
  const [newName, setNewName] = useState('');

  const selectedElement = elements.find(e => e.id === selectedId);
  const isDraggableTemplate = templateType === 'aldi_pallet_label' || templateType === 'agropur_pallet_label';

  // Aldi branch management
  const [aldiBranches, setAldiBranches] = useState([]);
  const [branchDialog, setBranchDialog] = useState(false);
  const [editBranch, setEditBranch] = useState(null); // null = add new, object = editing
  const [branchName, setBranchName] = useState('');
  const [branchAddress, setBranchAddress] = useState('');

  const loadData = useCallback(async (type) => {
    setLoading(true);
    try {
      const tmplRes = await api.get(`/templates?type=${type}`);
      setTemplates(tmplRes.data);
      const def = tmplRes.data.find(t => t.is_default) || tmplRes.data[0];
      if (def) {
        setCurrentTemplate(def);
        setElements(def.elements || []);
      } else {
        setCurrentTemplate(null);
        setElements([]);
      }
      // Load Aldi branches from settings
      if (type === 'aldi_pallet_label') {
        try {
          const settingsRes = await api.get('/settings');
          setAldiBranches(settingsRes.data?.aldi_branches || []);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(templateType); }, [templateType, loadData]);

  const handleSave = async () => {
    if (!currentTemplate) return;
    setSaving(true);
    try {
      await api.put(`/templates/${currentTemplate.id}`, { elements, name: currentTemplate.name });
      toast.success('Template saved');
      loadData(templateType);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const defRes = await api.get(`/templates/default-elements/${templateType}`);
      await api.post('/templates', {
        name: newName.trim(),
        type: templateType,
        elements: defRes.data.elements || [],
        is_default: templates.length === 0,
      });
      toast.success('Template created with default layout');
      setNewDialog(false);
      setNewName('');
      loadData(templateType);
    } catch { toast.error('Create failed'); }
  };

  const handleDuplicate = async () => {
    if (!currentTemplate) return;
    try {
      await api.post(`/templates/${currentTemplate.id}/duplicate`);
      toast.success('Template duplicated');
      loadData(templateType);
    } catch { toast.error('Duplicate failed'); }
  };

  const handleSetDefault = async () => {
    if (!currentTemplate) return;
    try {
      await api.post(`/templates/${currentTemplate.id}/set-default`);
      toast.success('Set as default');
      loadData(templateType);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    if (!currentTemplate) return;
    if (!window.confirm(`Delete "${currentTemplate.name}"?`)) return;
    try {
      await api.delete(`/templates/${currentTemplate.id}`);
      toast.success('Deleted');
      loadData(templateType);
    } catch { toast.error('Delete failed'); }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Reset all fields to default positions and values? This cannot be undone.')) return;
    try {
      const defRes = await api.get(`/templates/default-elements/${templateType}`);
      setElements(defRes.data.elements || []);
      toast.success('Reset to defaults (save to persist)');
    } catch { toast.error('Failed to load defaults'); }
  };

  // ── Aldi Branch CRUD ──
  const openAddBranch = () => {
    setEditBranch(null);
    setBranchName('');
    setBranchAddress('');
    setBranchDialog(true);
  };

  const openEditBranch = (branch) => {
    setEditBranch(branch);
    setBranchName(branch.name);
    setBranchAddress(branch.to_address);
    setBranchDialog(true);
  };

  const handleSaveBranch = async () => {
    if (!branchName.trim() || !branchAddress.trim()) return;
    let updated;
    if (editBranch) {
      updated = aldiBranches.map(b =>
        b.id === editBranch.id ? { ...b, name: branchName.trim(), to_address: branchAddress.trim() } : b
      );
    } else {
      const newBr = {
        id: `br-${Date.now()}`,
        name: branchName.trim(),
        to_address: branchAddress.trim(),
      };
      updated = [...aldiBranches, newBr];
    }
    try {
      await api.put('/settings', { aldi_branches: updated });
      setAldiBranches(updated);
      setBranchDialog(false);
      toast.success(editBranch ? 'Branch updated' : 'Branch added');
    } catch { toast.error('Failed to save branch'); }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Delete this branch?')) return;
    const updated = aldiBranches.filter(b => b.id !== branchId);
    try {
      await api.put('/settings', { aldi_branches: updated });
      setAldiBranches(updated);
      toast.success('Branch deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleVisibility = (id) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, visible: e.visible === false ? true : false } : e));
  };

  const handleToggleLock = (id) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, locked: !e.locked } : e));
  };

  const handleLockAll = () => {
    setElements(prev => prev.map(e => ({ ...e, locked: true })));
    toast.success('All fields locked');
  };

  const handleUnlockAll = () => {
    setElements(prev => prev.map(e => ({ ...e, locked: false })));
    toast.success('All fields unlocked');
  };

  const handleMoveField = (id, dir) => {
    setElements(prev => {
      const el = prev.find(e => e.id === id);
      if (!el) return prev;
      const sameSection = prev.filter(e => e.section === el.section).sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = sameSection.findIndex(e => e.id === id);
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= sameSection.length) return prev;
      const swapEl = sameSection[newIdx];
      return prev.map(e => {
        if (e.id === id) return { ...e, order: swapEl.order ?? newIdx };
        if (e.id === swapEl.id) return { ...e, order: el.order ?? idx };
        return e;
      });
    });
  };

  const handleUpdateElement = (updated) => {
    setElements(prev => prev.map(e => e.id === updated.id ? updated : e));
  };

  const handleUpdatePosition = (id, x_mm, y_mm) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, x_mm: Math.round(x_mm * 10) / 10, y_mm: Math.round(y_mm * 10) / 10 } : e));
  };

  const handleDeleteElement = () => {
    setElements(prev => prev.filter(e => e.id !== selectedId));
    setSelectedId(null);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const tabLabels = {
    dispatch_note: 'Dispatch Note',
    pallet_label: 'Pallet Label',
    aldi_pallet_label: 'Aldi Label',
    agropur_pallet_label: 'Agropur Label',
  };

  return (
    <div className="h-[calc(100vh-5rem)] flex flex-col" data-testid="template-designer">
      {/* Top bar */}
      <div className="flex items-center gap-3 pb-3 border-b mb-3 flex-wrap">
        <Tabs value={templateType} onValueChange={v => { setTemplateType(v); setSelectedId(null); }}>
          <TabsList className="h-8">
            <TabsTrigger value="dispatch_note" className="text-xs h-7 px-3" data-testid="tab-dispatch"><FileText className="w-3.5 h-3.5 mr-1" /> Dispatch Note</TabsTrigger>
            <TabsTrigger value="pallet_label" className="text-xs h-7 px-3" data-testid="tab-pallet"><Package className="w-3.5 h-3.5 mr-1" /> Standard</TabsTrigger>
          </TabsList>
        </Tabs>

        <Select value={currentTemplate?.id || ''} onValueChange={id => {
          const t = templates.find(t => t.id === id);
          if (t) { setCurrentTemplate(t); setElements(t.elements || []); setSelectedId(null); }
        }}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="template-selector"><SelectValue placeholder="Select template" /></SelectTrigger>
          <SelectContent>
            {templates.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name} {t.is_default && '\u2605'}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setNewDialog(true)} data-testid="btn-new-template">
          <Plus className="w-3.5 h-3.5 mr-1" /> New
        </Button>

        {currentTemplate && (
          <>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleDuplicate} data-testid="btn-duplicate">
              <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
            </Button>
            {!currentTemplate.is_default && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleSetDefault} data-testid="btn-set-default">
                <Star className="w-3.5 h-3.5 mr-1" /> Set Default
              </Button>
            )}
            {isDraggableTemplate && (
              <>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleLockAll} data-testid="btn-lock-all">
                  <Lock className="w-3.5 h-3.5 mr-1" /> Lock All
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleUnlockAll} data-testid="btn-unlock-all">
                  <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock All
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleResetToDefaults} data-testid="btn-reset-defaults">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 hover:bg-red-50" onClick={handleDelete} data-testid="btn-delete-template">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
            <div className="ml-auto">
              <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving} data-testid="btn-save-template">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </>
        )}
      </div>

      {!currentTemplate ? (
        <div className="flex-1 flex items-center justify-center">
          <Card className="w-80 text-center">
            <CardContent className="pt-6">
              <FileText className="w-12 h-12 mx-auto text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-500 mb-4">No templates yet for <strong>{tabLabels[templateType]}</strong>. Create your first template to start designing.</p>
              <Button onClick={() => setNewDialog(true)} data-testid="btn-create-first"><Plus className="w-4 h-4 mr-2" /> Create Template</Button>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="flex-1 flex gap-3 overflow-hidden">
          {/* Left sidebar – Field list */}
          <div className="w-56 flex-shrink-0 border-r pr-2">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider px-1 mb-2">Fields & Visibility</h3>
            <ScrollArea className="h-[calc(100vh-10rem)]">
              <FieldList
                elements={elements}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onToggle={handleToggleVisibility}
                onMove={handleMoveField}
                isDraggable={isDraggableTemplate}
                onToggleLock={handleToggleLock}
              />

              {/* Aldi Branches Section */}
              {templateType === 'aldi_pallet_label' && (
                <div className="mt-4 pt-3 border-t">
                  <div className="flex items-center justify-between px-1 mb-2">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Aldi Branches
                    </h4>
                    <button onClick={openAddBranch} className="text-blue-600 hover:bg-blue-50 rounded px-1 py-0.5 text-[10px] font-medium" data-testid="btn-add-branch">+ Add</button>
                  </div>
                  {aldiBranches.length === 0 ? (
                    <p className="text-[10px] text-zinc-400 px-1">No branches yet. Add an Aldi branch to enable quick label printing per location.</p>
                  ) : (
                    <div className="space-y-1">
                      {aldiBranches.map(b => (
                        <div key={b.id} className="flex items-center gap-1 px-1.5 py-1.5 rounded text-[11px] hover:bg-zinc-50 group" data-testid={`branch-item-${b.id}`}>
                          <MapPin className="w-3 h-3 text-zinc-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{b.name}</div>
                            <div className="text-[9px] text-zinc-400 truncate">{b.to_address?.split('\n')[0]}</div>
                          </div>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEditBranch(b)} className="p-0.5 hover:bg-zinc-200 rounded" title="Edit">
                              <PenLine className="w-3 h-3 text-zinc-500" />
                            </button>
                            <button onClick={() => handleDeleteBranch(b.id)} className="p-0.5 hover:bg-red-100 rounded" title="Delete">
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Center – WYSIWYG Preview */}
          <div className="flex-1 overflow-auto flex justify-center pt-2 pb-4 bg-zinc-100 rounded-lg">
            <div>
              <div className="text-[10px] text-zinc-500 mb-2 text-center font-medium">
                {isDraggableTemplate
                  ? 'A4 Preview \u2014 Drag fields to reposition, then Lock to freeze'
                  : 'A4 Document Preview \u2014 Click any field to edit its properties'}
              </div>
              {templateType === 'dispatch_note' && <DispatchNotePreview elements={elements} selectedId={selectedId} onSelect={setSelectedId} />}
              {templateType === 'pallet_label' && <PalletLabelPreview elements={elements} selectedId={selectedId} onSelect={setSelectedId} />}
              {templateType === 'aldi_pallet_label' && <AldiLabelPreview elements={elements} selectedId={selectedId} onSelect={setSelectedId} onUpdatePosition={handleUpdatePosition} />}
              {templateType === 'agropur_pallet_label' && <AgropurLabelPreview elements={elements} selectedId={selectedId} onSelect={setSelectedId} onUpdatePosition={handleUpdatePosition} />}
              <div className="text-[10px] text-zinc-400 mt-2 text-center">
                {elements.filter(e => e.visible !== false).length} / {elements.length} fields visible
                {isDraggableTemplate && ` \u2022 ${elements.filter(e => e.locked).length} locked`}
              </div>
            </div>
          </div>

          {/* Right sidebar – Properties */}
          <div className="w-56 flex-shrink-0 border-l pl-3">
            <ScrollArea className="h-[calc(100vh-10rem)]">
              <PropertiesPanel element={selectedElement} onChange={handleUpdateElement} onDelete={handleDeleteElement} isDraggableTemplate={isDraggableTemplate} />
            </ScrollArea>
          </div>
        </div>
      )}

      {/* New Template Dialog */}
      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>New Template</DialogTitle></DialogHeader>
          <div className="py-3">
            <Label>Template Name</Label>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g., Aldi Mitchelstown" className="mt-2"
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} autoFocus data-testid="new-template-name" />
            <p className="text-xs text-muted-foreground mt-2">
              Type: <strong>{tabLabels[templateType]}</strong>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              The template will be pre-loaded with the default {tabLabels[templateType]} layout.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newName.trim()} data-testid="btn-create-template">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Branch Add/Edit Dialog */}
      <Dialog open={branchDialog} onOpenChange={setBranchDialog}>
        <DialogContent className="sm:max-w-md" data-testid="branch-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editBranch ? 'Edit Branch' : 'Add Aldi Branch'}</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div>
              <Label>Branch Name</Label>
              <Input value={branchName} onChange={e => setBranchName(e.target.value)}
                placeholder="e.g., Mitchelstown, Naas, Atherstone..."
                className="mt-1" autoFocus data-testid="branch-name-input" />
            </div>
            <div>
              <Label>Delivery Address (To)</Label>
              <Textarea value={branchAddress} onChange={e => setBranchAddress(e.target.value)}
                placeholder="Aldi Stores (Ireland) Ltd.&#10;Mitchelstown&#10;Limerick Road&#10;IRL-P67 EY88&#10;Mitchelstown, Co. Cork"
                className="mt-1 min-h-[120px] text-sm" rows={5} data-testid="branch-address-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Each line becomes a separate address line on the label</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBranchDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveBranch} disabled={!branchName.trim() || !branchAddress.trim()} data-testid="btn-save-branch">
              {editBranch ? 'Update Branch' : 'Add Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
